import { useNavigate } from "react-router-dom";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Mail, Camera, BookOpen, BadgeCheck, type LucideIcon } from "lucide-react";

interface VerificationMethod {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  enabled: boolean;
  path?: string;
}

const verificationMethods: VerificationMethod[] = [
  {
    id: "school-email",
    title: "School Email",
    description: "Fastest way to verify",
    icon: Mail,
    enabled: true,
    path: "/verification/school-email",
  },
  {
    id: "classroom-photo",
    title: "Classroom Photo",
    description: "Upload a photo of classroom corner (no students)",
    icon: Camera,
    enabled: false,
  },
  {
    id: "teacher-questions",
    title: "Teacher Questions",
    description: "Answer teacher-only questions",
    icon: BookOpen,
    enabled: false,
  },
  {
    id: "teacher-id",
    title: "Teacher ID Upload",
    description: "Upload cropped ID card",
    icon: BadgeCheck,
    enabled: false,
  },
];

export function VerificationMethod() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleCardClick = (method: VerificationMethod) => {
    if (method.enabled && method.path) {
      navigate(method.path);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    method: VerificationMethod
  ) => {
    if ((e.key === "Enter" || e.key === " ") && method.enabled && method.path) {
      e.preventDefault();
      navigate(method.path);
    }
  };

  const handleSkip = () => {
    toast({
      title: "Features Limited",
      description: "Without verification, privacy features will be locked.",
      variant: "destructive",
    });
    // Go to profile creation flow
    navigate("/profile/photos");
  };

  return (
    <main
      className="min-h-screen bg-brand-background p-4"
      role="main"
      aria-labelledby="verification-heading"
    >
      <div className="max-w-2xl mx-auto py-8">
        <h1
          id="verification-heading"
          className="text-3xl font-bold text-center mb-2 text-brand-text"
        >
          Verify You're a Teacher
        </h1>
        <p className="text-brand-muted text-center mb-8">
          Choose one verification method to unlock privacy protections
        </p>

        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          role="group"
          aria-label="Verification methods"
        >
          {verificationMethods.map((method) => {
            const Icon = method.icon;
            return (
              <Card
                key={method.id}
                className={`transition-all bg-brand-surface border-brand-border ${
                  method.enabled
                    ? "cursor-pointer hover:border-brand-primary hover:shadow-md focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:outline-none"
                    : "opacity-50 cursor-not-allowed"
                }`}
                onClick={() => handleCardClick(method)}
                onKeyDown={(e) => handleKeyDown(e, method)}
                role="button"
                aria-disabled={!method.enabled}
                aria-label={`${method.title}: ${method.description}${!method.enabled ? " - Coming Soon" : ""}`}
                tabIndex={method.enabled ? 0 : -1}
              >
                <CardHeader className="flex flex-row items-center gap-4">
                  <Icon
                    className={`h-8 w-8 ${method.enabled ? "text-brand-primary" : "text-brand-muted"}`}
                    aria-hidden="true"
                  />
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2 text-brand-text">
                      {method.title}
                      {!method.enabled && (
                        <Badge variant="secondary">Coming Soon</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="text-brand-muted">{method.description}</CardDescription>
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <Button variant="ghost" onClick={handleSkip}>
            Skip for Now
          </Button>
        </div>
      </div>
    </main>
  );
}
